import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { generateUniqueId } from '../utils/unique-id';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Interface to ensure broadcast message uniformity.
 */
export interface IBroadcastMessage {
    action: string;
    payload?: any;
}

type MessageWithSource<M extends IBroadcastMessage> = M & { source: string };

export class BroadcastController<Message extends IBroadcastMessage = IBroadcastMessage> implements ReactiveController {
    private host: ReactiveControllerHost;
    private channel: BroadcastChannel;

    accessor sourceId = generateUniqueId();
    accessor onMessage: ((message: MessageWithSource<Message>) => void) | null = null;
    accessor onError: ((error: any) => void) | null = null;

    enableSending = true;

    constructor(host: ReactiveControllerHost, channelId: string) {
        this.host = host;
        this.host.addController(this);

        this.channel = new BroadcastChannel(channelId);
        this.registerOnMessageHandler();
    }

    private registerOnMessageHandler() {
        if (this.channel) {
            // Handles errors in the broadcast.
            this.channel.onmessageerror = (me) => {
                if (this.onError) {
                    this.onError({
                        source: me.source,
                        origin: me.origin,
                        data: me.data
                    });
                }
            };

            // Handles incoming messages and relays them to the registered handler.
            this.channel.onmessage = (me: MessageEvent<MessageWithSource<Message>>) => {
                if (this.onMessage) {
                    this.onMessage(me.data);
                }
            };
        }
    }

    send(message: Message) {
        if (this.channel && this.enableSending) {
            this.channel.postMessage({ source: this.sourceId, ...message } as MessageWithSource<Message>);
        }
    }

    hostDisconnected() {
        this.channel.close();
    }
}
